import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import { GameName, GameState, Role } from '../enums';
import type { BinaryReader } from '../BinaryReader';
import { decodeGameInfoModel, writeGameInfoModel, type GameInfoModel } from '../models/gameInfo';
import { decodePlayerModel, type PlayerModel } from '../models/player';
import { decodeNewGameConfig, type NewGameConfig } from '../models/newGameConfig';

/**
 * Matchmaking codec: create game, search/join, the ready handshake, and the table-entry messages.
 * Mirrors MatchmakingModule (server) and SearchGame.cs / WaitListItem.cs / NewGameUi.cs (Unity).
 */

// ---- Create game ----

/** C2S GetNewGameConfigs(68): one short = the GameName to fetch creation options for. */
export function encodeGetNewGameConfigs(gameName: GameName): Uint8Array {
  return beginMessage(MessageTag.GetNewGameConfigs).putShort(gameName).toUint8Array();
}

/** S2C GetNewGameConfigs(68): a NewGameConfig (dropdowns + price range). */
export function decodeNewGameConfigs(r: BinaryReader): NewGameConfig {
  return decodeNewGameConfig(r);
}

/** C2S NewGame(48): the GameInfoModel the player wants to create. */
export function encodeNewGame(info: GameInfoModel): Uint8Array {
  const w = beginMessage(MessageTag.NewGame);
  writeGameInfoModel(w, info);
  return w.toUint8Array();
}

// ---- Search / join ----

/** C2S SearchGame(40): empty body — request the joinable-table list. */
export function encodeSearchGame(): Uint8Array {
  return beginMessage(MessageTag.SearchGame).toUint8Array();
}

export interface SearchGameEntry {
  game: GameInfoModel;
  player1: PlayerModel;
  /** Present only when the table is Running or Recovered (a second seat is filled). */
  player2: PlayerModel | null;
}

/**
 * S2C SearchGame(40): an INT count (hand-rolled, NOT a ushort PutArray), then per entry a
 * GameInfoModel + Player1, plus a second PlayerModel iff GameState is Running or Recovered.
 */
export function decodeSearchGame(r: BinaryReader): SearchGameEntry[] {
  const count = r.getInt();
  const out: SearchGameEntry[] = new Array(count);
  for (let i = 0; i < count; i++) {
    const game = decodeGameInfoModel(r);
    const player1 = decodePlayerModel(r);
    const hasP2 = game.gameState === GameState.Running || game.gameState === GameState.Recovered;
    out[i] = { game, player1, player2: hasP2 ? decodePlayerModel(r) : null };
  }
  return out;
}

/** C2S JoinGame(42): the UniqueId of a Wait-state table to join. */
export function encodeJoinGame(uniqueId: string): Uint8Array {
  return beginMessage(MessageTag.JoinGame).putString(uniqueId).toUint8Array();
}

/** C2S WatchGame(43): the UniqueId of a Running table to spectate. */
export function encodeWatchGame(uniqueId: string): Uint8Array {
  return beginMessage(MessageTag.WatchGame).putString(uniqueId).toUint8Array();
}

/** C2S ExitView(11): stop spectating the current game (empty body). */
export function encodeExitView(): Uint8Array {
  return beginMessage(MessageTag.ExitView).toUint8Array();
}

// ---- Ready handshake + table entry ----

/** The WriteGameInfo payload shared by IsReady(21), WaitReady(22) and (with a trailing bool) GameInfo(58). */
export interface GameInfoPayload {
  game: GameInfoModel;
  player1: PlayerModel;
  player2: PlayerModel;
  player1TotalWin: number; // short
  player2TotalWin: number; // short
  myRole: Role; // short
}

/** Exported for reuse by the Backgammon StartGamePanel(54) decoder, which is WriteGameInfo + extras. */
export function decodeGameInfoPayload(r: BinaryReader): GameInfoPayload {
  return {
    game: decodeGameInfoModel(r),
    player1: decodePlayerModel(r),
    player2: decodePlayerModel(r),
    player1TotalWin: r.getShort(),
    player2TotalWin: r.getShort(),
    myRole: r.getShort() as Role,
  };
}

/** S2C IsReady(21): shown to the table creator (P1) when someone joins — confirm/cancel dialog. */
export const decodeIsReady = decodeGameInfoPayload;

/** S2C WaitReady(22): shown to the joiner (P2) — waiting-for-opponent dialog. */
export const decodeWaitReady = decodeGameInfoPayload;

/** S2C GameInfo(58): WriteGameInfo + a trailing bool isPreview. This is the table-entry message. */
export interface GameInfoSeat extends GameInfoPayload {
  isPreview: boolean;
}

export function decodeGameInfo(r: BinaryReader): GameInfoSeat {
  return { ...decodeGameInfoPayload(r), isPreview: r.getBool() };
}

/**
 * Remembers a locally requested WatchGame until its GameInfo arrives. Some deployed servers have
 * omitted or briefly sent a stale `isPreview=false`; matching the requested table id keeps the
 * resulting scene read-only without ever turning an unrelated seated game into a preview.
 */
export class SpectatorIntent {
  private pendingUniqueId: string | null = null;

  begin(uniqueId: string): void {
    this.pendingUniqueId = uniqueId;
  }

  resolve(seat: GameInfoSeat): GameInfoSeat {
    const locallyRequested = this.pendingUniqueId === seat.game.uniqueId;
    this.pendingUniqueId = null;
    return seat.isPreview || !locallyRequested ? seat : { ...seat, isPreview: true };
  }

  /** Exposed for diagnostics/tests; normal callers only use begin() and resolve(). */
  get pending(): string | null {
    return this.pendingUniqueId;
  }
}

/** C2S Ready(44): creator confirms the game start (empty body — MessageTools does NOT append the id here). */
export function encodeReady(): Uint8Array {
  return beginMessage(MessageTag.Ready).toUint8Array();
}

/** C2S NotReady(45): decline the ready prompt. MessageTools appends the current game's UniqueId. */
export function encodeNotReady(uniqueId: string): Uint8Array {
  return beginMessage(MessageTag.NotReady).putString(uniqueId).toUint8Array();
}

/** C2S CanselGame(55): the creator cancels a Wait-state table before anyone joins (empty body). */
export function encodeCancelGame(): Uint8Array {
  return beginMessage(MessageTag.CanselGame).toUint8Array();
}

/** S2C ShowNextDealer(72): dealer-selection reveal, sent to both seats as the table starts. */
export interface NextDealer {
  player1Hand: string;
  player2Hand: string;
  dealerRole: Role;
}

export function decodeShowNextDealer(r: BinaryReader): NextDealer {
  return {
    player1Hand: r.getString(),
    player2Hand: r.getString(),
    dealerRole: r.getShort() as Role,
  };
}

/** C2S ReadyToPlay(73): this client's in-scene countdown finished (empty body). */
export function encodeReadyToPlay(): Uint8Array {
  return beginMessage(MessageTag.ReadyToPlay).toUint8Array();
}

/** C2S ResignGame(47): forfeit/leave the current game (empty body). */
export function encodeResignGame(): Uint8Array {
  return beginMessage(MessageTag.ResignGame).toUint8Array();
}
